import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useGrnDetailContext } from 'providers/GrnDetailProvider';
import { useUserContext } from 'providers/UserProvider';

import useFetchById from 'hooks/useFetchById';

import SubtleBadge from 'components/common/SubtleBadge';
import GrnViewMode from 'components/purchase-order/grn/GrnDetails/GrnViewMode';

import { grnApi } from 'api';
import { formatStatus } from 'utils/valueFormatters';
import { getGrnBadgeColor, getGrnTypeBadgeColor } from 'constants/grn';
import { getGrnTypeFromDetail } from 'constants/grn/grnType';

const GrnDetailInfo = ({ listenToUpdates = false }) => {
  const { grnId } = useParams();
  const { updateGrnData } = useGrnDetailContext();
  const { selectedWarehouse, warehouse_permissions } = useUserContext();

  const currentPermission = warehouse_permissions?.find(
    p => p.warehouse?.id === selectedWarehouse?.id,
  );
  const canApproveGRN = currentPermission?.put_away >= 2;

  const [requestLoading, setRequestLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  // Get GRN data
  const {
    data: grn,
    loading: fetchLoading,
    error: fetchError,
    setData: setGrn,
    refetch: grnRefetch,
  } = useFetchById(grnApi, grnId);

  useEffect(() => {
    if (grn) {
      updateGrnData(grn);
    }
  }, [grn, updateGrnData]);

  useEffect(() => {
    if (listenToUpdates) {
      const handleUpdate = () => {
        grnRefetch();
      };

      window.addEventListener('grn-line-updated', handleUpdate);

      return () => window.removeEventListener('grn-line-updated', handleUpdate);
    }
  }, [listenToUpdates, grnRefetch]);

  // GRN 승인 요청
  const handleRequestApprove = async () => {
    setRequestLoading(true);

    try {
      const response = await grnApi.updateGRNStatus(grnId, {
        grn_status: 'pending',
      });

      toast.success('Request approved successfully');

      await grnRefetch();
    } catch (err) {
      toast.error('Failed to request approval');
    } finally {
      setRequestLoading(false);
    }
  };

  // GRN 요청 승인
  const handleApproveRequest = async () => {
    setApproveLoading(true);

    try {
      const response = await grnApi.updateGRNStatus(grnId, {
        grn_status: 'approved',
      });

      toast.success('Approved successfully');

      await grnRefetch();
    } catch (err) {
      toast.error('Failed to approve');
    } finally {
      setApproveLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Card className="h-100">
        <Card.Body>
          <div className="text-center">Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{fetchError}</Alert>
        </Card.Body>
      </Card>
    );
  }

  const grnType = getGrnTypeFromDetail(grn);

  return (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0 me-1">
            GRN List {'>'} #{grn?.id}
          </h5>

          <SubtleBadge pill bg={getGrnBadgeColor(grn?.grn_status)}>
            {formatStatus(grn?.grn_status)}
          </SubtleBadge>

          {grnType && (
            <SubtleBadge pill bg={getGrnTypeBadgeColor(grnType)}>
              {formatStatus(grnType)}
            </SubtleBadge>
          )}
        </div>

        <div className="d-flex gap-2">
          {canApproveGRN && grn?.grn_status === 'pending' && (
            <Button
              variant="primary"
              size="sm"
              disabled={approveLoading}
              onClick={handleApproveRequest}
            >
              Approve GRN
            </Button>
          )}

          {grn?.grn_status === 'draft' && grn?.received_quantity > 0 && (
            <Button
              variant="primary"
              size="sm"
              disabled={requestLoading}
              onClick={handleRequestApprove}
            >
              Request Approval
            </Button>
          )}
        </div>
      </div>

      <Card>
        <Card.Body>
          <GrnViewMode grn={grn} />
        </Card.Body>
      </Card>
    </div>
  );
};

export default GrnDetailInfo;
